import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, ButtonToolbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { ActiveFilterViewer, Filters } from '@rankmi/rankmi-table'

import { CourseStateType } from 'enum'
import { IMainState } from 'store/states'
import { ICoursesState } from 'store/states/courses.state'
import * as fromActions from 'store/actions/courses.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import { generateFilterViewerLocale } from 'helpers/translations.helper'
import { PathRoutes } from 'enum/routes'
import { useAccess } from 'contexts/AccessContext'
import getPublishedNormativeWithoutAttestationCount from 'api/administration/courses/GetPublishedNormativeWithoutAttestationCount'
import CourseList from './components/courseList'
import { Actions } from './components/Actions'
import NormativeCoursesAboutButton from './components/NormativeCoursesAboutButton'
import PublishedNormativeWithoutAttestationAlert from './components/PublishedNormativeWithoutAttestationAlert'

const Index: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { download } = useSelector(
    ({ courses }: IMainState) => courses as ICoursesState
  )
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const [
    publishedNormativeWithoutAttestationCount,
    setPublishedNormativeWithoutAttestationCount,
  ] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({} as any)
  const [filtersColumns, setFilterColumns] = useState([] as any)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState({})
  const [perPage, setPerPage] = useState(50)
  const [selectedRows, setSelectedRows] = useState([] as any)
  const [fastFilters, setFastFilters] = useState(0 as any)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const prefix = 'administration.courses.list.create.table.headers'
  const searchTextRef = useRef<any>()
  const filtersRef = useRef()
  const { allProfilesList, loading: profilesLoading } = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles
  )
  const [translations, setTranslations] = useState<any>()
  useEffect(() => {
    setTranslations(() => generateFilterViewerLocale())
  }, [])
  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  useEffect(() => {
    //  setLoading(true)
    searchTextRef.current = searchText
    filtersRef.current = selectedFilters

    const timer = setTimeout(() => {
      dispatch({
        type: fromActions.COURSES_ADMINISTRATION_GET_LIST,
        payload: {
          page,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
      })
    }, 400)
    return () => clearTimeout(timer)
  }, [page, sort, searchText, perPage, selectedFilters, fastFilters, dispatch])

  useEffect(() => {
    if (!profilesLoading) {
      setFilterColumns([
        {
          label: t(`${prefix}.name`),
          value: 'search_name',
          type: 'string',
        },
        {
          label: t(`${prefix}.forMandatoryProfiles`),
          value: 'required_profiles_id',
          type: 'tagsList',
          list: allProfilesList.map(
            ({ id: value, name: label, color }: any) => ({
              value,
              label,
              color,
            })
          ),
        },
        {
          label: t(`${prefix}.forProfiles`),
          value: 'optional_profiles_id',
          type: 'tagsList',
          list: allProfilesList.map(
            ({ id: value, name: label, color }: any) => ({
              value,
              label,
              color,
            })
          ),
        },
        {
          label: t(`${prefix}.createAt`),
          value: 'created_at',
          type: 'date',
        },
        {
          label: t(`${prefix}.state`),
          value: 'state',
          type: 'list',
          list: [
            CourseStateType.Draft,
            CourseStateType.Published,
            CourseStateType.Imparted,
            CourseStateType.Soon,
            CourseStateType.Inactive,
          ].map((state) => ({
            label: t(`commons.course.state.${CourseStateType[state]}`),
            value: state.toString(),
          })),
        },
        {
          label: t(`${prefix}.courseType.synchronousCourse`),
          value: 'synchronous',
          type: 'boolean',
        },
      ])
    }
  }, [allProfilesList, profilesLoading, t])

  const {
    loading,
    modules: { administration },
    access,
  } = useAccess()
  const canManageCourses = access.canAccessCourseManagementView()
  const canManageCoursesForNormative = access.canManageCourses()
  const canInternalCoursesList = access.canAccessInternalCoursesList()

  useEffect(() => {
    if (loading) return
    if (canInternalCoursesList) return
    if (access.hasExternalCourseAccess()) {
      history.replace(PathRoutes.EXTERNAL_COURSES)
      return
    }
    if (!(administration || canManageCourses)) {
      history.push(PathRoutes.MAIN)
    }
  }, [
    loading,
    canInternalCoursesList,
    access,
    administration,
    canManageCourses,
    history,
  ])

  const handleSearch = (value: string) => {
    setPage(1)
    setSearchText(value.trim())
  }

  const handleDelete = (id: number) => {
    dispatch({
      type: fromActions.DESTROY_COURSE,
      payload: {
        id,
        params: {
          page,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
      },
    })
  }

  useEffect(() => {
    download !== '' && dispatch({ type: fromActions.CLEAR_COURSES_DOWNLOAD })
  }, [dispatch, download])

  useEffect(() => {
    // Contador/alerta normativa DC3: solo quien puede gestionar cursos (BE: verify_manage_courses!).
    // Usuarios con solo viewCourseDetails entran al listado pero no usan esta UI.
    if (!attestationsEnabled || !canManageCoursesForNormative) {
      setPublishedNormativeWithoutAttestationCount(0)
      return
    }

    getPublishedNormativeWithoutAttestationCount()
      .then(({ count }) => setPublishedNormativeWithoutAttestationCount(count))
      .catch(() => setPublishedNormativeWithoutAttestationCount(0))
  }, [attestationsEnabled, canManageCoursesForNormative])

  return (
    <>
      <Row className="align-items-center" style={{ marginBottom: 16 }}>
        <Col className="d-flex align-items-center justify-content-between">
          <h1 className="mb-0">
            {t('administration.courses.list.managementTitle')}
          </h1>
          {attestationsEnabled && canManageCoursesForNormative ? (
            <NormativeCoursesAboutButton />
          ) : null}
        </Col>
      </Row>
      {attestationsEnabled && publishedNormativeWithoutAttestationCount > 0 ? (
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <PublishedNormativeWithoutAttestationAlert
              count={publishedNormativeWithoutAttestationCount}
            />
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col>
          <ButtonToolbar className="float-left">
            <Filters
              columns={filtersColumns}
              onChange={(data: any) => setSelectedFilters(data)}
              open={showFiltersModal}
              onVisibilityChange={(state: boolean) =>
                setShowFiltersModal(state)
              }
              locale={translations}
            />
          </ButtonToolbar>
          <Actions
            {...{
              allSelected,
              selectedRows,
              searchText,
              selectedFilters,
              fastFilters,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <ActiveFilterViewer
            activeFilters={selectedFilters}
            onChange={(data: any) => setSelectedFilters(data)}
            openFilters={(state: boolean) => setShowFiltersModal(state)}
            columns={filtersColumns}
            locale={translations}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CourseList
            onDelete={handleDelete}
            setSearch={handleSearch}
            setPage={setPage}
            setSort={setSort}
            setPerPage={setPerPage}
            setSelectedRows={setSelectedRows}
            setFastFilters={setFastFilters}
            setAllSelected={setAllSelected}
          />
        </Col>
      </Row>
    </>
  )
}

export default Index
